import {useCallback, useEffect, useMemo, useState} from 'react'
import {Card, Form, Button, Spinner, Table, Alert, Badge} from 'react-bootstrap'
import axios from 'axios'
import {toast} from 'react-toastify'
import {useIntl} from 'react-intl'
import {URL as BACKEND_URL} from '../../../gql/client'
import {STORAGE_NAMES} from '../../../helpers/consts'
import {SUPERVISOR_MESSAGES as M} from '../supervisorMessages'

type Target = {id: number; name: string; email?: string}
type CampaignRow = {
  campaign_id: string
  name: string
  active: string
  all_users: boolean
  group_ids: number[]
  user_ids: number[]
}
type Payload = {
  campaigns: CampaignRow[]
  groups: Target[]
  users: Target[]
  enforced: boolean
}
type EditState = {all_users: boolean; group_ids: number[]; user_ids: number[]}

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem(STORAGE_NAMES.TOKEN)}`,
  'Content-Type': 'application/json',
})

// Admin: assign which VICIdial campaigns each group/user (or everyone) can see.
export const CampaignAccessTab = () => {
  const intl = useIntl()
  const [data, setData] = useState<Payload | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [edits, setEdits] = useState<Record<string, EditState>>({})
  const [savingId, setSavingId] = useState('')
  const [togglingEnforce, setTogglingEnforce] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const {data: res} = await axios.get<Payload>(
        `${BACKEND_URL}/api/telephony/supervisor/campaign-assignments`,
        {headers: authHeaders()}
      )
      setData(res)
      const seed: Record<string, EditState> = {}
      ;(res.campaigns || []).forEach((c) => {
        seed[c.campaign_id] = {all_users: c.all_users, group_ids: c.group_ids || [], user_ids: c.user_ids || []}
      })
      setEdits(seed)
      setError('')
    } catch (err: any) {
      setError(
        err?.response?.data?.error || err?.message || intl.formatMessage(M.accessLoadFailed)
      )
    } finally {
      setLoading(false)
    }
  }, [intl])

  useEffect(() => {
    load()
  }, [load])

  const groups = data?.groups ?? []
  const users = data?.users ?? []

  const multiValues = (e: any): number[] =>
    Array.from(e.target.selectedOptions as HTMLCollectionOf<HTMLOptionElement>).map((o) => Number(o.value))

  const setEdit = (cid: string, patch: Partial<EditState>) =>
    setEdits((prev) => ({...prev, [cid]: {...prev[cid], ...patch}}))

  const save = async (cid: string) => {
    const e = edits[cid]
    if (!e) return
    setSavingId(cid)
    try {
      await axios.post(
        `${BACKEND_URL}/api/telephony/supervisor/campaign-assignments`,
        {campaign_id: cid, all_users: e.all_users, group_ids: e.group_ids, user_ids: e.user_ids},
        {headers: authHeaders()}
      )
      toast.success(intl.formatMessage(M.accessSaved, {campaign: cid}))
      await load()
    } catch (err: any) {
      toast.error(
        err?.response?.data?.error || err?.message || intl.formatMessage(M.accessSaveFailed)
      )
    } finally {
      setSavingId('')
    }
  }

  const toggleEnforce = async () => {
    const next = !data?.enforced
    if (next && !window.confirm(intl.formatMessage(M.accessConfirmEnforce))) return
    setTogglingEnforce(true)
    try {
      await axios.post(
        `${BACKEND_URL}/api/telephony/supervisor/campaign-visibility-enforce`,
        {enabled: next},
        {headers: authHeaders()}
      )
      setData((prev) => (prev ? {...prev, enforced: next} : prev))
      toast.success(
        intl.formatMessage(next ? M.accessEnforceEnabled : M.accessEnforceDisabled)
      )
    } catch (err: any) {
      toast.error(
        err?.response?.data?.error ||
          err?.message ||
          intl.formatMessage(M.accessEnforceChangeFailed)
      )
    } finally {
      setTogglingEnforce(false)
    }
  }

  const summary = useMemo(() => {
    if (!data) return ''
    const assigned = data.campaigns.filter((c) => c.all_users || c.group_ids.length || c.user_ids.length).length
    return intl.formatMessage(M.accessSummary, {
      assigned: String(assigned),
      total: String(data.campaigns.length),
    })
  }, [intl, data])

  return (
    <div className='mt-4'>
      <Card className='shadow-sm mb-3'>
        <Card.Body className='d-flex justify-content-between align-items-center flex-wrap gap-3'>
          <div>
            <div className='fw-bold fs-5'>{intl.formatMessage(M.accessTitle)}</div>
            <div className='text-muted small'>
              {intl.formatMessage(M.accessSubtitle)} {summary}
            </div>
          </div>
          <div className='d-flex align-items-center gap-2'>
            <Form.Check
              type='switch'
              id='enforce-campaign-visibility'
              label={intl.formatMessage(M.accessEnforceLabel)}
              checked={!!data?.enforced}
              disabled={togglingEnforce}
              onChange={toggleEnforce}
            />
            {data?.enforced ? (
              <Badge bg='success'>{intl.formatMessage(M.accessEnforceOn)}</Badge>
            ) : (
              <Badge bg='secondary'>{intl.formatMessage(M.accessEnforceOff)}</Badge>
            )}
          </div>
        </Card.Body>
      </Card>

      {data?.enforced && (
        <Alert variant='warning' className='py-2'>
          {intl.formatMessage(M.accessEnforceNotice)}
        </Alert>
      )}

      <Card className='shadow-sm'>
        <Card.Body className='p-0'>
          {loading && !data ? (
            <div className='text-center py-10'><Spinner animation='border' size='sm' /></div>
          ) : error ? (
            <Alert variant='danger' className='m-3'>{error}</Alert>
          ) : (
            <Table responsive hover className='align-middle mb-0'>
              <thead>
                <tr>
                  <th className='ps-4'>{intl.formatMessage(M.accessColCampaign)}</th>
                  <th style={{width: 90}}>{intl.formatMessage(M.accessColAll)}</th>
                  <th>{intl.formatMessage(M.accessColGroups)}</th>
                  <th>{intl.formatMessage(M.accessColUsers)}</th>
                  <th className='text-end pe-4'></th>
                </tr>
              </thead>
              <tbody>
                {(data?.campaigns ?? []).map((c) => {
                  const e = edits[c.campaign_id] || {all_users: false, group_ids: [], user_ids: []}
                  return (
                    <tr key={c.campaign_id}>
                      <td className='ps-4'>
                        <div className='fw-semibold'>{c.name || c.campaign_id}</div>
                        <div className='text-muted fs-8'>
                          {c.campaign_id}
                          {String(c.active).toUpperCase() === 'Y' ? (
                            <span className='badge badge-light-success ms-1'>
                              {intl.formatMessage(M.accessActiveBadge)}
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td>
                        <Form.Check
                          type='switch'
                          id={`all-${c.campaign_id}`}
                          checked={e.all_users}
                          onChange={(ev) => setEdit(c.campaign_id, {all_users: ev.target.checked})}
                        />
                      </td>
                      <td>
                        <Form.Select
                          multiple
                          size='sm'
                          htmlSize={4}
                          disabled={e.all_users}
                          value={e.group_ids.map(String)}
                          onChange={(ev) => setEdit(c.campaign_id, {group_ids: multiValues(ev)})}
                        >
                          {groups.map((g) => (
                            <option key={g.id} value={g.id}>{g.name}</option>
                          ))}
                        </Form.Select>
                      </td>
                      <td>
                        <Form.Select
                          multiple
                          size='sm'
                          htmlSize={4}
                          disabled={e.all_users}
                          value={e.user_ids.map(String)}
                          onChange={(ev) => setEdit(c.campaign_id, {user_ids: multiValues(ev)})}
                        >
                          {users.map((u) => (
                            <option key={u.id} value={u.id}>{u.name}</option>
                          ))}
                        </Form.Select>
                      </td>
                      <td className='text-end pe-4'>
                        <Button
                          size='sm'
                          variant='primary'
                          disabled={savingId === c.campaign_id}
                          onClick={() => save(c.campaign_id)}
                        >
                          {savingId === c.campaign_id ? (
                            <Spinner animation='border' size='sm' />
                          ) : (
                            intl.formatMessage(M.save)
                          )}
                        </Button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
      <div className='text-muted fs-8 mt-2'>{intl.formatMessage(M.accessTip)}</div>
    </div>
  )
}
